import React from 'react';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { classNamesFunction, Icon, Image, ImageFit, Link, Spinner, Stack, Text } from '@fluentui/react';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { ICredentialDialogStyles, styles } from './CredentialDialog.styles';
import { AUTHENTICATOR_LINK } from '../../shared/constants';
import { isMobile } from '../../shared/utils';
import { ICredentialError, IVerifiedCredential } from './Credential.type';
import { getVerifyIdErrorString } from '../../shared/getErrorString';
import { useAuth } from '@iamexperiences/feature-auth';

interface ICredentialDialogProps {
  isLoading?: boolean;
  credential?: IVerifiedCredential;
  error?: ICredentialError;
  onGenerateNewOne: () => void;
  onCopyLink: () => void;
  onDone: () => void;
}

const getClassNames = classNamesFunction<{}, ICredentialDialogStyles>();

export const CredentialDialog: React.FunctionComponent<ICredentialDialogProps> = ({
  isLoading,
  credential,
  error,
  onGenerateNewOne: generateClick,
  onCopyLink,
  onDone,
}) => {
  const [t] = useLocalization('Account');
  const { auth } = useAuth();

  const classNames = getClassNames(styles);
  const hideQrCode = isMobile();

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        type: DialogType.normal,
        showCloseButton: false,
      }}
      modalProps={{ isBlocking: true, styles: { main: styles.main } }}
    >
      <div className={classNames.content}>
        {isLoading && (
          <Stack tokens={{ maxWidth: 50 }}>
            <Spinner ariaLabel={t('dialogProcessingRequest')} ariaLive="assertive" />
          </Stack>
        )}

        {error && (
          <div>
            <Icon iconName="Warning" /> {getVerifyIdErrorString(t, error.type, auth.user?.tenantId)}
          </div>
        )}

        {credential && (
          <>
            <Text className={classNames.description}>
              {' '}
              {hideQrCode ? t('tabButtonToRetrieve') : t('qrCredentialDescription')}
            </Text>
            {hideQrCode && <PrimaryButton text={t('getMyVerifiedId')} href={credential.url} />}
            {!hideQrCode && (
              <>
                <div className={classNames.imageSection}>
                  <Image src={credential.qrCode} className={classNames.image} imageFit={ImageFit.centerContain} />
                  <Text>
                    {t('credentialExpiration')} <Link onClick={generateClick}>{t('generateANewOne')}</Link>
                  </Text>
                </div>

                <DefaultButton text={t('cantScanImage')} onClick={onCopyLink} />
              </>
            )}
            <Link href={AUTHENTICATOR_LINK} target="_blank">
              {t('downloadMicrosoftAuthenticator')} <Icon iconName="OpenInNewWindow" />
            </Link>
            <Text className={classNames.centeredText}>
              {t('verifiedIdCloseDialogWarning')}
            </Text>
          </>
        )}
      </div>
      {!hideQrCode && (
        <DialogFooter>
          <PrimaryButton disabled={isLoading} onClick={onDone} text={credential ? t('done') : t('closeButton')} />
        </DialogFooter>
      )}
    </Dialog>
  );
};
